import { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { apiRequest, queryClient } from '@/lib/queryClient';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Input } from '@/components/ui/input';
import { useToast } from '@/hooks/use-toast';
import {
  Mail,
  Send,
  Plus,
  Inbox,
  Clock,
  User,
  AlertCircle,
  CheckCircle2,
  Paperclip,
  Search,
  RefreshCw,
  MessageSquare,
  FileText,
} from 'lucide-react';
import { format } from 'date-fns';
import { useRef } from 'react';
import { EmailConversationView } from './EmailConversationView';
import { EmailComposer } from './EmailComposer';
import { EmailSearchFilter, EmailFilters } from './EmailSearchFilter';
import { useEmailKeyboardShortcuts, emailKeyboardShortcuts } from '@/hooks/useEmailKeyboardShortcuts';

interface EmailTabProps {
  customerId: number;
  customerName?: string;
  customerEmail?: string;
}

interface EmailConversation {
  id: number;
  customerId: number;
  threadId: string;
  subject: string;
  participants: string[];
  messageCount: number;
  lastMessageAt: string;
  status: string;
  createdAt: string;
  updatedAt: string;
  customerName?: string;
  customerEmail?: string;
}

type EmailView = 'conversations' | 'conversation' | 'compose';

export function EmailTab({ customerId, customerName, customerEmail }: EmailTabProps) {
  const { toast } = useToast();
  const [currentView, setCurrentView] = useState<EmailView>('conversations');
  const [selectedConversation, setSelectedConversation] = useState<number | null>(null);
  const [selectedConversationIndex, setSelectedConversationIndex] = useState<number>(0);
  const [showShortcuts, setShowShortcuts] = useState(false);
  const [showSearchFilter, setShowSearchFilter] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Search filter state
  const [filters, setFilters] = useState<EmailFilters>({
    searchQuery: '',
    status: 'all',
    hasAttachments: undefined,
    dateRange: undefined,
    sender: undefined,
    recipient: undefined,
    sortBy: 'date',
    sortOrder: 'desc',
  });

  // Check Microsoft Graph connection status (application-level)
  const { data: connectionStatus, isLoading: connectionLoading } = useQuery({
    queryKey: ['/api/emails/connection-status'],
    queryFn: () =>
      apiRequest<{ isConnected: boolean; mailboxCount?: number }>(
        'GET',
        '/api/emails/connection-status'
      ),
  });

  // Build query parameters from filters
  const buildQueryParams = () => {
    const params = new URLSearchParams();
    params.append('customerId', customerId.toString());

    if (filters.searchQuery) params.append('searchQuery', filters.searchQuery);
    if (filters.status !== 'all') params.append('status', filters.status);
    if (filters.hasAttachments) params.append('hasAttachments', 'true');
    if (filters.dateRange?.from) params.append('dateFrom', filters.dateRange.from.toISOString());
    if (filters.dateRange?.to) params.append('dateTo', filters.dateRange.to.toISOString());
    if (filters.sender) params.append('sender', filters.sender);
    if (filters.recipient) params.append('recipient', filters.recipient);
    params.append('sortBy', filters.sortBy);
    params.append('sortOrder', filters.sortOrder);

    return params.toString();
  };

  // Fetch conversations for this customer with filters
  const {
    data: conversations,
    isLoading: conversationsLoading,
    error: conversationsError,
    refetch: refetchConversations,
  } = useQuery({
    queryKey: ['/api/emails/conversations', { customerId, filters }],
    queryFn: () =>
      apiRequest<EmailConversation[]>('GET', `/api/emails/conversations?${buildQueryParams()}`),
    enabled: !!customerId,
  });

  // Manual email sync mutation
  const syncEmailsMutation = useMutation({
    mutationFn: () => apiRequest('POST', '/api/emails/sync-now'),
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: ['/api/emails/conversations'] });
      toast({
        title: 'Email Sync Complete',
        description: data.message || `Processed ${data.conversationsProcessed || 0} conversations`,
      });
    },
    onError: (error) => {
      toast({
        title: 'Sync Error',
        description: `Failed to sync emails: ${error.message}`,
        variant: 'destructive',
      });
    },
  });

  // Conversations are already filtered by the backend based on filters
  const filteredConversations = conversations || [];

  const handleConversationSelect = (conversationId: number, index?: number) => {
    setSelectedConversation(conversationId);
    if (index !== undefined) {
      setSelectedConversationIndex(index);
    }
    setCurrentView('conversation');
  };

  const handleCompose = () => {
    setCurrentView('compose');
  };

  const handleBackToConversations = () => {
    setCurrentView('conversations');
    setSelectedConversation(null);
  };

  // Keyboard shortcuts
  useEmailKeyboardShortcuts({
    enabled: currentView !== 'compose',
    onCompose: handleCompose,
    onSearch: () => {
      // Focus on the search field in the EmailSearchFilter
      const searchInput = document.querySelector('input[placeholder*="Search emails"]') as HTMLInputElement;
      searchInput?.focus();
    },
    onRefresh: () => syncEmailsMutation.mutate(),
    onNextConversation: () => {
      if (filteredConversations.length > 0) {
        const nextIndex = Math.min(selectedConversationIndex + 1, filteredConversations.length - 1);
        const nextConversation = filteredConversations[nextIndex];
        handleConversationSelect(nextConversation.id, nextIndex);
      }
    },
    onPreviousConversation: () => {
      if (filteredConversations.length > 0) {
        const prevIndex = Math.max(selectedConversationIndex - 1, 0);
        const prevConversation = filteredConversations[prevIndex];
        handleConversationSelect(prevConversation.id, prevIndex);
      }
    },
  });

  // Show connection testing or error state
  if (connectionLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5" />
            Email Communications
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col items-center gap-4 p-8 text-center">
            <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-full">
              <Mail className="h-8 w-8 text-blue-600 dark:text-blue-400 animate-pulse" />
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-2">Loading Email System</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Checking Microsoft Graph connection...
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Show error state if connection fails
  if (connectionStatus && !connectionStatus.isConnected) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5" />
            Email Communications
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col items-center gap-4 p-8 text-center">
            <div className="p-3 bg-orange-50 dark:bg-orange-900/20 rounded-full">
              <AlertCircle className="h-8 w-8 text-orange-600 dark:text-orange-400" />
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-2">Email Integration Issue</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Microsoft Graph connection could not be established. Please check configuration.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Main two-panel layout with improved spacing
  return (
    <div className="h-[calc(100vh-180px)] flex gap-4">
      {/* Left Panel - Conversations List - Increased width */}
      <div className="w-[400px] flex flex-col border rounded-lg bg-card">
        <div className="p-4 border-b">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <MessageSquare className="h-5 w-5" />
              Conversations
            </h2>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => syncEmailsMutation.mutate()}
                disabled={syncEmailsMutation.isPending}
                className="p-2"
              >
                <RefreshCw
                  className={`h-4 w-4 ${syncEmailsMutation.isPending ? 'animate-spin' : ''}`}
                />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleCompose}
                className="flex items-center gap-2"
              >
                <Plus className="h-4 w-4" />
                Compose
              </Button>
            </div>
          </div>

          {/* Search Filter */}
          <EmailSearchFilter
            filters={filters}
            onFiltersChange={setFilters}
            onSearch={() => refetchConversations()}
            resultCount={filteredConversations.length}
          />

          {/* Keyboard shortcuts help */}
          <div className="flex justify-end mt-2">
            <button
              onClick={() => setShowShortcuts(!showShortcuts)}
              className="text-xs text-muted-foreground hover:text-foreground"
              title="Keyboard shortcuts"
            >
              ⌘K for shortcuts
            </button>
          </div>
        </div>

        <ScrollArea className="flex-1">
          <div className="p-2">
            {conversationsLoading ? (
              <div className="space-y-2">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="p-3 border rounded-lg animate-pulse">
                    <div className="h-4 bg-muted rounded w-3/4 mb-2"></div>
                    <div className="h-3 bg-muted rounded w-1/2"></div>
                  </div>
                ))}
              </div>
            ) : filteredConversations.length === 0 ? (
              <div className="p-8 text-center text-muted-foreground">
                <Mail className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p className="text-sm">
                  {conversations?.length === 0
                    ? 'No email conversations found for this customer'
                    : 'No conversations match your search'}
                </p>
              </div>
            ) : (
              <div className="space-y-1">
                {filteredConversations.map((conversation, idx) => (
                  <Card
                    key={conversation.id}
                    className={`cursor-pointer transition-all hover:bg-muted/50 hover:shadow-md ${
                      selectedConversation === conversation.id
                        ? 'bg-muted border-primary shadow-md'
                        : ''
                    }`}
                    onClick={() => handleConversationSelect(conversation.id, idx)}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start gap-3">
                        {/* Conversation Avatar */}
                        <div className="flex-shrink-0">
                          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-semibold text-sm">
                            {conversation.customerName
                              ? conversation.customerName
                                  .split(' ')
                                  .map(n => n[0])
                                  .slice(0, 2)
                                  .join('')
                                  .toUpperCase()
                              : conversation.participants[0]?.substring(0, 2).toUpperCase() || 'UN'
                            }
                          </div>
                        </div>

                        {/* Conversation Details */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between mb-1">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <h4 className="font-semibold text-sm truncate">
                                  {conversation.customerName || conversation.participants[0] || 'Unknown'}
                                </h4>
                                {conversation.status === 'active' && (
                                  <div className="h-2 w-2 bg-green-500 rounded-full flex-shrink-0 animate-pulse"></div>
                                )}
                                {conversation.messageCount > 0 && (
                                  <Badge variant="secondary" className="text-xs px-1.5 py-0">
                                    {conversation.messageCount}
                                  </Badge>
                                )}
                              </div>
                              <p className="font-medium text-sm truncate mt-0.5">
                                {conversation.subject || 'No Subject'}
                              </p>
                            </div>
                            <div className="text-xs text-muted-foreground flex-shrink-0 ml-2">
                              {format(new Date(conversation.lastMessageAt), 'MMM d')}
                            </div>
                          </div>

                          {/* Preview or participant list */}
                          <p className="text-xs text-muted-foreground truncate mt-1">
                            {conversation.participants.length > 1
                              ? `${conversation.participants.length} participants`
                              : conversation.participants[0]
                            }
                          </p>

                          {/* Tags/Status */}
                          <div className="flex items-center gap-2 mt-2">
                            {conversation.messageCount > 5 && (
                              <Badge variant="outline" className="text-xs">
                                Long thread
                              </Badge>
                            )}
                            {new Date(conversation.lastMessageAt) > new Date(Date.now() - 24 * 60 * 60 * 1000) && (
                              <Badge variant="outline" className="text-xs bg-green-50 dark:bg-green-950 text-green-700 dark:text-green-300 border-green-200 dark:border-green-800">
                                Recent
                              </Badge>
                            )}
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </ScrollArea>
      </div>

      {/* Right Panel - Email View or Compose */}
      <div className="flex-1">
        {currentView === 'compose' ? (
          <EmailComposer
            customerId={customerId}
            customerName={customerName}
            customerEmail={customerEmail}
            onClose={handleBackToConversations}
          />
        ) : currentView === 'conversation' && selectedConversation ? (
          <EmailConversationView
            conversationId={selectedConversation}
            customerId={customerId}
            onBack={handleBackToConversations}
          />
        ) : (
          <Card className="h-full">
            <CardContent className="flex flex-col items-center justify-center h-full text-center">
              <div className="p-6 bg-muted/30 rounded-full mb-6">
                <Mail className="h-16 w-16 text-muted-foreground" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Select a conversation</h3>
              <p className="text-muted-foreground mb-6 max-w-md">
                Choose a conversation from the list to view messages, or compose a new email to
                start a conversation.
              </p>
              <div className="flex gap-3">
                <Button onClick={handleCompose} className="flex items-center gap-2">
                  <Plus className="h-4 w-4" />
                  Compose New Email
                </Button>
                <Button
                  variant="outline"
                  onClick={() => syncEmailsMutation.mutate()}
                  disabled={syncEmailsMutation.isPending}
                  className="flex items-center gap-2"
                >
                  <RefreshCw
                    className={`h-4 w-4 ${syncEmailsMutation.isPending ? 'animate-spin' : ''}`}
                  />
                  Sync Emails
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Keyboard Shortcuts Modal */}
      {showShortcuts && (
        <div
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center"
          onClick={() => setShowShortcuts(false)}
        >
          <div
            className="bg-card p-6 rounded-lg shadow-xl max-w-md w-full mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-semibold mb-4">Keyboard Shortcuts</h3>
            <div className="space-y-2">
              {emailKeyboardShortcuts.map((shortcut) => (
                <div key={shortcut.key} className="flex justify-between text-sm">
                  <span className="text-muted-foreground">{shortcut.description}</span>
                  <kbd className="px-2 py-1 bg-muted rounded text-xs font-mono">
                    {shortcut.key}
                  </kbd>
                </div>
              ))}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowShortcuts(false)}
              className="mt-4 w-full"
            >
              Close
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
